import { UserState } from "@/auth/user"
import { isSuperCoffeeUser } from "@/utils"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { StatusBreakdown } from "@/deduction/StatusBreakdown.tsx"
import { StatusBreakdownByMonth } from "@/deduction/StatusBreakdownByMonth.tsx"
import { ReasonCodeBreakdownByFlag } from "@/deduction/SuperCoffeeReasonCodeBreakdownByFlag.tsx"
import { CategoryBreakdown } from "@/deduction/CategoryBreakdown.tsx"
import { TaskBreakdown } from "@/deduction/TaskBreakdown.tsx"
import { DeductionListJson } from "@/api"
import { GlobalFilterState } from "@/global_filter"
import { useState } from "preact/compat"

interface TableBreakdownProps {
  data: DeductionListJson[]
}

type StageView = "total" | "monthly"

function StageViewToggle({ view, onSelect }: { view: StageView; onSelect: (view: StageView) => void }) {
  const options: { value: StageView; label: string }[] = [
    { value: "total", label: "Total" },
    { value: "monthly", label: "By Month" },
  ]
  return (
    <div role="tablist" aria-label="Stage breakdown view" class="flex items-center rounded-full bg-slate-200 p-0.5">
      {options.map(option => (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={view === option.value}
          onClick={() => onSelect(option.value)}
          class={`cursor-pointer rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap transition-colors ${
            view === option.value ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

export function TableBreakdowns({ data }: TableBreakdownProps) {
  const user = UserState.use(u => u!)
  const isSuperCoffee = user && isSuperCoffeeUser(user.org_id)
  const [stageView, setStageView] = useState<StageView>("total")
  const { startDate, endDate, dateKind } = GlobalFilterState.use(gf => gf!)

  return (
    <div className="grid grid-cols-3 gap-4 mb-6 mt-8">
      <div className="space-y-4">
        <Card className="h-fit">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-md font-medium">Deduction Stage Breakdown</CardTitle>
            <div className="flex items-center gap-2">
              <StageViewToggle view={stageView} onSelect={setStageView} />
              {stageView === "total" && <div>Gross Deductions</div>}
            </div>
          </CardHeader>
          <CardContent>
            {stageView === "monthly" ? (
              <StatusBreakdownByMonth data={data} dateKind={dateKind} startDate={startDate} endDate={endDate} />
            ) : (
              <StatusBreakdown data={data} />
            )}
          </CardContent>
        </Card>

        {isSuperCoffee && (
          <Card className="h-fit">
            <CardHeader className="flex space-y-0 pb-2">
              <CardTitle className="text-md font-medium">Reason Code Breakdown by Flag</CardTitle>
            </CardHeader>
            <CardContent>
              <ReasonCodeBreakdownByFlag />
            </CardContent>
          </Card>
        )}
      </div>

      <Card className="h-fit">
        <CardHeader className="flex space-y-0 pb-2">
          <CardTitle className="text-md font-medium">Deduction Category Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <CategoryBreakdown data={data} />
        </CardContent>
      </Card>

      <Card className="h-fit">
        <CardHeader className="flex space-y-0 pb-2">
          <CardTitle className="text-md font-medium">Task Status Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <TaskBreakdown data={data} />
        </CardContent>
      </Card>
    </div>
  )
}

export interface Bar2 {
  key: string
  amount: number
  name: string
  value: number
  color: string
}
